import React, { ChangeEvent, Component } from 'react';
import FormicaSearchSelectBox, { FormicaSelectBoxItem } from '../FormicaSearchSelectBox';
import FormicaInput from '../FormicaInput';
import { FormicaTimePickerDate, QuickDate, QuickDateTypes, RecentlyUsedDate } from './TimePickerDomain';
import DatePickerUtil from './DatePickerUtil';
import LanguageUtil from '../../../util/LanguageUtil';

class DatePickerQuickSelectComponent extends Component<Props, State> {
  public state = new State();

  render() {
    const { duration, timeUnit } = this.state;
    return (
      <div className={'flex flex-col w-full gap-3'}>
        <div className={'flex flex-col gap-y-1 justify-center items-start'}>
          <p className={'text-f-black-1 text-sm font-normal tracking-wider'}>
            {LanguageUtil.get({ id: 'common.label.commonlyUsed', defaultMessage: 'Commonly Used' })}
          </p>
          <div className={'flex flex-col max-h-[100px] flex-wrap gap-y-1 justify-between w-full'}>{this.getCommonlyUsedDates()}</div>
        </div>
        <div className={'flex flex-col gap-y-1 justify-center items-start'}>
          <p className={'text-f-black-1 text-sm font-normal tracking-wider'}>
            {LanguageUtil.get({ id: 'common.label.recentlyUsed', defaultMessage: 'Recently Used' })}
          </p>
          <div className={'grid grid-cols-3 w-full gap-y-1 gap-x-1'}>{this.getRecentlyUsedDates()}</div>
        </div>
        <div className={'flex flex-col gap-y-1 justify-center items-start'}>
          <p className={'text-f-black-1 text-sm font-normal tracking-wider'}>{LanguageUtil.get({ id: 'common.label.last', defaultMessage: 'Last' })}</p>
        </div>
        <div className={'flex w-full gap-x-4 items-center'}>
          <FormicaInput
            placeholder={LanguageUtil.get({ id: 'common.label.time', defaultMessage: 'Time' })}
            type={'number'}
            className={'text-2xs'}
            onChange={(e: ChangeEvent<HTMLInputElement>) => this.onChangeDuration(e)}
            value={duration}
          />
          <FormicaSearchSelectBox
            value={timeUnit}
            placeholder={LanguageUtil.get({ id: 'common.input.placeholder.min', defaultMessage: 'Min' })}
            options={this.getTimeOptions()}
            onChange={(e: FormicaSelectBoxItem) => this.onChangeTimeUnit(e)}
          />
          <div onClick={this.applyDate} className={'flex items-center justify-center text-sm cursor-pointer w-1/2 h-8 rounded bg-primary text-white'}>
            {LanguageUtil.get({ id: 'common.label.apply', defaultMessage: 'Apply' })}
          </div>
        </div>
      </div>
    );
  }

  private onClickCommonlyUsed = (startDate: FormicaTimePickerDate, endDate: FormicaTimePickerDate) => {
    const { onApply } = this.props;
    onApply(startDate, endDate);
  };

  private applyDate = () => {
    const { duration, timeUnit } = this.state;
    if (!duration || isNaN(duration) || duration <= 0) return;
    let label: string | null = null;

    switch (timeUnit) {
      case 'second':
        label = LanguageUtil.get({ id: 'common.label.lastSecond', defaultMessage: `Last ${duration} second` }, { 0: `${duration}` });
        break;
      case 'minute':
        label = LanguageUtil.get({ id: 'common.label.lastMinute', defaultMessage: `Last ${duration} minutes` }, { 0: `${duration}` });
        break;
      case 'hour':
        label = LanguageUtil.get({ id: 'common.label.lastHour', defaultMessage: `Last ${duration} hour` }, { 0: `${duration}` });
        break;
      case 'day':
        label = LanguageUtil.get({ id: 'common.label.lastDay', defaultMessage: `Last ${duration} day` }, { 0: `${duration}` });
        break;
      case 'week':
        label = LanguageUtil.get({ id: 'common.label.lastWeek', defaultMessage: `Last ${duration} week` }, { 0: `${duration}` });
        break;
      case 'month':
        label = LanguageUtil.get({ id: 'common.label.lastMonth', defaultMessage: `Last ${duration} month` }, { 0: `${duration}` });
        break;
      case 'year':
        label = LanguageUtil.get({ id: 'common.label.lastYear', defaultMessage: `Last ${duration} year` }, { 0: `${duration}` });
        break;
      default:
        break;
    }
    if (label) {
      const startDate: FormicaTimePickerDate = {
        label,
        value: DatePickerUtil.calculateQuickValue(duration, timeUnit),
        type: 'Quick',
      };
      const endDate: FormicaTimePickerDate = { label: LanguageUtil.get({ id: 'common.label.now', defaultMessage: 'Now' }), value: 0, type: 'Now' };
      this.setQuickDate(startDate, endDate);
    }
  };

  private onChangeTimeUnit = (e: FormicaSelectBoxItem) => {
    this.setState({ timeUnit: e.value });
  };

  private onChangeDuration = (e: ChangeEvent<HTMLInputElement>) => {
    this.setState({ duration: parseInt(e.target.value) });
  };
  private getTimeOptions = () => {
    return [
      {
        label: LanguageUtil.get({ id: 'common.label.second', defaultMessage: 'Second' }),
        value: 'second',
      },
      {
        label: LanguageUtil.get({ id: 'common.label.minute', defaultMessage: 'Minute' }),
        value: 'minute',
      },
      {
        label: LanguageUtil.get({ id: 'common.label.hour', defaultMessage: 'Hour' }),
        value: 'hour',
      },
      {
        label: LanguageUtil.get({ id: 'common.label.day', defaultMessage: 'Day' }),
        value: 'day',
      },
      {
        label: LanguageUtil.get({ id: 'common.label.week', defaultMessage: 'Week' }),
        value: 'week',
      },
      {
        label: LanguageUtil.get({ id: 'common.label.month', defaultMessage: 'Month' }),
        value: 'month',
      },
      {
        label: LanguageUtil.get({ id: 'common.label.year', defaultMessage: 'Year' }),
        value: 'year',
      },
    ];
  };

  private setQuickDate = (startDate: FormicaTimePickerDate, endDate: FormicaTimePickerDate) => {
    const { setEndDate, setStartDate, onApply } = this.props;
    setStartDate(startDate);
    setEndDate(endDate);
    onApply(startDate, endDate);
  };

  private getCommonlyUsedDates = () => {
    const dates: Record<QuickDateTypes, QuickDate> = new DatePickerUtil().getAllDates();
    return Object.entries(dates).map((entry, index) => {
      const label = entry[0];
      const dates: QuickDate = entry[1];
      return (
        <div key={index}>
          <a
            onClick={() => this.onClickCommonlyUsed(dates.startDate, dates.endDate)}
            className={'text-md text-[#458EF7] col-span-1 cursor-pointer first-letter:uppercase'}
          >
            <p className={'first-letter:uppercase'}>{label?.split('_').join(' ').toLowerCase()}</p>
          </a>
        </div>
      );
    });
  };

  private getRecentlyUsedDates = () => {
    const recentlyUsedDates = localStorage.getItem('dates');
    if (!recentlyUsedDates)
      return (
        <div className={'flex w-full h-full items-center justify-center text-sm col-span-3'}>
          <p className={'text-yellow-500'}>
            {LanguageUtil.get({ id: 'common.label.thereIsNoRecentlyUsedDates', defaultMessage: 'There is no recently used dates.' })}
          </p>
        </div>
      );
    const dates: RecentlyUsedDate[] = JSON.parse(recentlyUsedDates);
    return dates?.map((date: RecentlyUsedDate, index: number) => {
      return (
        <a
          onClick={() => this.onClickCommonlyUsed(date.startDate, date.endDate)}
          className={'text-md text-[#458EF7] flex w-full truncate hover:cursor-pointer'}
          key={index}
        >
          {date.startDate.label}
          {'->'}
          {date.endDate.label}
        </a>
      );
    });
  };
}

class State {
  duration: number = 30;
  timeUnit: string = 'minute';
}

interface Props {
  startDate: FormicaTimePickerDate;
  endDate: FormicaTimePickerDate;
  setStartDate: (date: FormicaTimePickerDate) => void;
  setEndDate: (date: FormicaTimePickerDate) => void;
  onApply: (startDate: FormicaTimePickerDate, endDate: FormicaTimePickerDate) => void;
}

export default DatePickerQuickSelectComponent;
